import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LogOut, Eye, Settings, Mail, QrCode, Wine, Ticket, Users, UserPlus, Share2
} from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import PasswordInput from "@/components/common/PasswordInput";
import EventsTab from "@/components/admin/tabs/EventsTab";
import ScannerUsersTab from "@/components/admin/tabs/ScannerUsersTab";
import AdminUsersTab from "@/components/admin/tabs/AdminUsersTab";
import AforoUsersTab from "@/components/admin/tabs/AforoUsersTab";
import GestoresTab from "@/components/admin/tabs/GestoresTab";
import RrppTab from "@/components/admin/tabs/RrppTab";
import MesasTab from "@/components/admin/tabs/MesasTab";
import BonocopasTab from "@/components/admin/tabs/BonocopasTab";
import NewsletterTab from "@/components/admin/tabs/NewsletterTab";
import ConfigTab from "@/components/admin/tabs/ConfigTab";

const AdminPanel = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [isGestor, setIsGestor] = useState(false);
  const [gestorPrivileges, setGestorPrivileges] = useState({
    puede_gestionar_eventos: false,
    puede_ver_aforo: false,
    puede_escanear: false
  });
  const [gestorEventos, setGestorEventos] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState("events"); // "events", "users", "admins", "bonocopas", "newsletter"
  const [savedCredentials, setSavedCredentials] = useState("");
  const navigate = useNavigate();

  const credentials = savedCredentials || btoa(`${loginForm.username}:${loginForm.password}`);

  const fetchEvents = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const config = { headers: { Authorization: `Basic ${credentials}` } };
      // Use admin/events endpoint which filters by gestor's assigned events
      const response = await axios.get(`${API}/admin/events?include_inactive=true`, config);
      setEvents(response.data);
    } catch (e) {
      console.error("Error fetching events:", e);
      // Fallback to public endpoint if admin endpoint fails
      try {
        const response = await axios.get(`${API}/events?include_inactive=true`);
        let eventsList = response.data;
        if (isGestor && gestorEventos && gestorEventos.length > 0) {
          eventsList = eventsList.filter(event => gestorEventos.includes(event.id));
        }
        setEvents(eventsList);
      } catch (e2) {
        console.error("Error fetching events (fallback):", e2);
      }
    }
  }, [isLoggedIn, isGestor, gestorEventos, credentials]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    
    const currentCredentials = btoa(`${loginForm.username}:${loginForm.password}`);
    
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      if (response.data.success) {
        const { role, redirect_url, nombre } = response.data;
        
        // If scanner or aforo, redirect to their specific panel
        if (role && (role === "scanner" || role === "aforo") && redirect_url) {
          // Store credentials for the target panel
          localStorage.setItem('taytantos_credentials', currentCredentials);
          localStorage.setItem('taytantos_user_role', role);
          localStorage.setItem('taytantos_user_nombre', nombre);
          // Redirect to the appropriate panel
          navigate(redirect_url);
          return;
        }
        
        // Admin/Superuser/Gestor - stay on admin panel
        setSavedCredentials(currentCredentials);
        
        // Get user info to determine role and permissions
        try {
          const userInfo = await axios.get(`${API}/admin/me`, {
            headers: { Authorization: `Basic ${currentCredentials}` }
          });
          setCurrentUser(userInfo.data);
          setIsSuperuser(userInfo.data.is_superuser === true);
          
          // Check if gestor
          if (userInfo.data.role === 'gestor') {
            setIsGestor(true);
            setGestorPrivileges(userInfo.data.privileges || {});
            setGestorEventos(userInfo.data.eventos_asignados || []);
          }
        } catch (err) {
          // Fallback based on login response
          setIsSuperuser(role === "superuser");
          if (role === "gestor") {
            setIsGestor(true);
          }
        }
        
        setIsLoggedIn(true);
      }
    } catch (e) {
      setLoginError("Credenciales incorrectas");
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setIsSuperuser(false);
    setIsGestor(false);
    setGestorPrivileges({ puede_gestionar_eventos: false, puede_ver_aforo: false, puede_escanear: false });
    setGestorEventos([]);
    setCurrentUser(null);
    setLoginForm({ username: "", password: "" });
    setSavedCredentials("");
  };

  // Set correct active tab for gestor based on privileges
  useEffect(() => {
    if (isGestor && isLoggedIn) {
      // Find first available tab for gestor
      if (gestorPrivileges.puede_gestionar_eventos) {
        setActiveTab("events");
      } else if (gestorPrivileges.puede_ver_aforo) {
        setActiveTab("aforo");
      } else if (gestorPrivileges.puede_escanear) {
        setActiveTab("users");
      } else {
        setActiveTab("rrpp");
      }
    }
  }, [isGestor, isLoggedIn, gestorPrivileges]);

  if (!isLoggedIn) {
    return (
      <div className="hero-bg min-h-screen flex items-center justify-center">
        <LaserBackground />
        <div className="form-section max-w-md w-full mx-4 relative z-10">
          <h2 className="font-heading text-2xl text-white mb-6 text-center uppercase">
            Admin Login
          </h2>
          
          {loginError && (
            <div className="error-message mb-4">{loginError}</div>
          )}
          
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Usuario</label>
              <input
                type="text"
                className="custom-input"
                value={loginForm.username}
                onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                data-testid="admin-username"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <PasswordInput
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                testId="admin-password"
              />
            </div>
            <button type="submit" className="btn-primary mt-4" data-testid="admin-login-btn">
              Entrar
            </button>
          </form>
          
          <button 
            onClick={() => navigate('/')}
            className="w-full mt-4 text-[var(--text-muted)] hover:text-white text-sm"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <div className="main-container-wide py-8 relative z-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="relative inline-block">
              <h1 className="font-logo text-2xl text-white neon-glow">TAYTANTOS</h1>
              <span 
                className="absolute text-white neon-glow text-xl"
                style={{fontFamily: "'Monoton', cursive", right: '-8px', top: '100%', marginTop: '-10px'}}
              >
                pass
              </span>
            </div>
            <p className="text-[var(--text-muted)] text-sm mt-4">Panel de Administración</p>
          </div>
          <div className="flex gap-4 items-center">
            {/* User info */}
            <div className="text-right mr-4 border-r border-[var(--border-color)] pr-4">
              <p className="text-white text-sm font-medium">
                {currentUser?.nombre || loginForm.username}
              </p>
              <p className={`text-xs ${isSuperuser ? 'text-yellow-400' : isGestor ? 'text-orange-400' : 'text-[var(--accent-cyan)]'}`}>
                {isSuperuser ? 'SUPERUSUARIO' : isGestor ? 'GESTOR' : 'ADMINISTRADOR'}
              </p>
            </div>
            {/* Scanner link - hide for gestors without permission */}
            {(!isGestor || gestorPrivileges.puede_escanear) && (
              <button 
                onClick={() => navigate('/scanner')}
                className="btn-download text-sm"
                data-testid="scanner-link"
              >
                <QrCode size={16} className="inline mr-2" />
                Escáner QR
              </button>
            )}
            <button 
              onClick={() => navigate('/')}
              className="btn-download text-sm"
            >
              <Eye size={16} className="inline mr-2" />
              Ver Web
            </button>
            <button 
              onClick={handleLogout}
              className="text-[var(--text-muted)] hover:text-[var(--error)] flex items-center gap-2"
              data-testid="admin-logout"
            >
              <LogOut size={18} />
              Salir
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[var(--border-color)] overflow-x-auto">
          {/* Events Tab - visible if admin/superuser OR gestor with puede_gestionar_eventos */}
          {(!isGestor || gestorPrivileges.puede_gestionar_eventos) && (
            <button
              onClick={() => setActiveTab("events")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "events" 
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)]" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-events"
            >
              <Ticket size={16} className="inline mr-2" />
              Eventos
            </button>
          )}
          {/* Scanner Users Tab - for admin/superuser or gestor with puede_escanear */}
          {(!isGestor || gestorPrivileges.puede_escanear) && (
            <button
              onClick={() => setActiveTab("users")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "users" 
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)]" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-users"
            >
              <Users size={16} className="inline mr-2" />
              Usuarios Escáner
            </button>
          )}
          {isSuperuser && (
            <button
              onClick={() => setActiveTab("admins")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "admins" 
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)]" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-admins"
            >
              <Settings size={16} className="inline mr-2" />
              Administradores
            </button>
          )}
          {/* Aforo Tab - visible if admin/superuser OR gestor with puede_ver_aforo */}
          {(!isGestor || gestorPrivileges.puede_ver_aforo) && (
            <button
              onClick={() => setActiveTab("aforo")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "aforo" 
                  ? "text-[var(--accent-cyan)] border-b-2 border-[var(--accent-cyan)]" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-aforo"
            >
              <Eye size={16} className="inline mr-2" />
              Control Aforo
            </button>
          )}
          {/* Gestores Tab - only for admin/superuser */}
          {!isGestor && (
            <button
              onClick={() => setActiveTab("gestores")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "gestores" 
                  ? "text-orange-400 border-b-2 border-orange-400" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-gestores"
            >
              <UserPlus size={16} className="inline mr-2" />
              Gestores
            </button>
          )}
          {/* RRPP Tab - for admin/superuser or gestor with assigned events */}
          {(!isGestor || (gestorEventos && gestorEventos.length > 0)) && (
            <button
              onClick={() => setActiveTab("rrpp")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "rrpp" 
                  ? "text-[var(--primary)] border-b-2 border-[var(--primary)]" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-rrpp"
            >
              <Share2 size={16} className="inline mr-2" />
              RRPP
            </button>
          )}
          {/* Mesas Tab - only for admin/superuser */}
          {!isGestor && (
            <button
              onClick={() => setActiveTab("mesas")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "mesas" 
                  ? "text-yellow-400 border-b-2 border-yellow-400" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-mesas"
            >
              <Users size={16} className="inline mr-2" />
              Mesas/Reservados
            </button>
          )}
          {/* Bonocopas Tab - only for admin/superuser */}
          {!isGestor && (
            <button
              onClick={() => setActiveTab("bonocopas")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "bonocopas" 
                  ? "text-yellow-500 border-b-2 border-yellow-500" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-bonocopas"
            >
              <Wine size={16} className="inline mr-2" />
              Bonocopas
            </button>
          )}
          {/* Newsletter Tab - only for admin/superuser */}
          {!isGestor && (
            <button
              onClick={() => setActiveTab("newsletter")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "newsletter" 
                  ? "text-green-400 border-b-2 border-green-400" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-newsletter"
            >
              <Mail size={16} className="inline mr-2" />
              Newsletter
            </button>
          )}
          {/* Scanner Link - for gestors with puede_escanear */}
          {isGestor && gestorPrivileges.puede_escanear && (
            <button
              onClick={() => navigate('/scanner')}
              className="pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap text-green-400 hover:text-green-300"
              data-testid="gestor-scanner-link"
            >
              <QrCode size={16} className="inline mr-2" />
              Ir a Escáner
            </button>
          )}
          {isSuperuser && (
            <button
              onClick={() => setActiveTab("config")}
              className={`pb-3 px-2 text-sm font-bold uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === "config" 
                  ? "text-yellow-400 border-b-2 border-yellow-400" 
                  : "text-[var(--text-muted)] hover:text-white"
              }`}
              data-testid="tab-config"
            >
              <Settings size={16} className="inline mr-2" />
              Configuración
            </button>
          )}
        </div>
        {activeTab === "events" && (!isGestor || gestorPrivileges.puede_gestionar_eventos) && <EventsTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "users" && (!isGestor || gestorPrivileges.puede_escanear) && <ScannerUsersTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "admins" && isSuperuser && <AdminUsersTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "aforo" && <AforoUsersTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "gestores" && <GestoresTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "rrpp" && <RrppTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "mesas" && <MesasTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "bonocopas" && <BonocopasTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "newsletter" && <NewsletterTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
        {activeTab === "config" && isSuperuser && <ConfigTab credentials={credentials} events={events} fetchEvents={fetchEvents} isGestor={isGestor} gestorPrivileges={gestorPrivileges} isSuperuser={isSuperuser} setActiveTab={setActiveTab} />}
      </div>
    </div>
  );
};

export default AdminPanel;
